import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import ProductService, { isProductListTimeout } from "../../../../sdk/services/ProductService";
import { pickLandingPlans } from "../lib/landingPlans";
import type { LandingPlan } from "../lib/landingPlans";

export const LANDING_PLANS_TIMEOUT_MS = 8000;

export type LandingPlansStatus = "loading" | "ready" | "empty" | "error" | "timeout";

export interface LandingPlansState {
  status: LandingPlansStatus;
  /** Selected plans when status is "ready". [] for loading, empty, error, and timeout. */
  plans: LandingPlan[];
  /** Sets status to "loading", clears plans, and starts a new GET. */
  retry: () => void;
}

function isCallerCancellation(err: unknown): boolean {
  if (axios.isCancel(err)) return true;
  if (typeof err !== "object" || err === null || !("code" in err)) return false;
  return (err as { code?: unknown }).code === "ERR_CANCELED";
}

export function useLandingPlans(): LandingPlansState {
  // This hook loads the raw product list and passes it to pickLandingPlans.
  // It does not call usePlans or fromProduct, because that merge fills an
  // empty description and an empty feature list from the static catalog.
  // `plans[].price` stays the number from the payload. A later render formats it.
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<LandingPlansStatus>("loading");
  const [plans, setPlans] = useState<LandingPlan[]>([]);
  // Bumped in retry before the previous request can settle, so a late
  // response cannot replace "loading" with the attempt that was just cancelled.
  const attemptRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);

  const retry = useCallback(() => {
    attemptRef.current += 1;
    controllerRef.current?.abort();
    setStatus("loading");
    setPlans([]);
    setAttempt(attemptRef.current);
  }, []);

  useEffect(() => {
    const requestAttempt = attemptRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    let alive = true;
    setStatus("loading");
    setPlans([]);

    ProductService.getAll({
      timeoutMs: LANDING_PLANS_TIMEOUT_MS,
      signal: controller.signal,
    })
      .then((products) => {
        if (!alive || attemptRef.current !== requestAttempt) return;
        const selected = pickLandingPlans(products);
        if (selected.length === 0) {
          setPlans([]);
          setStatus("empty");
          return;
        }
        setPlans(selected);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (!alive || attemptRef.current !== requestAttempt) return;
        setPlans([]);
        if (isProductListTimeout(err)) {
          setStatus("timeout");
          return;
        }
        if (isCallerCancellation(err)) return;
        setStatus("error");
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [attempt]);

  return { status, plans, retry };
}
